import * as _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ShareBlock } from './share-block';
import { BarChartBlock } from './bar-chart';
import { InfoBlock } from './info-block';
import { UsageHighlightsBlock } from './usage-highlights-block';
import { AnalyticsTabs } from '../analytics-tabs';
import { getAnalyticsAPI, getAnalytics, getAnalyticsOverviewAPI, getAnalyticsOverview, getAnalyticsFilter, setAnalyticsFilter, setAnalyticsDatePeriod, getAnalyticsDatePeriod } from '../../../store/features/analytics';
import { selectInstitution, selectUser, } from '../../../store/features/system';
import { DatepickerBlock } from './datepicker-block';
import { ColorService } from 'common/services/color-service';
import { TimeConverter } from 'utility/time-converter';
import { AnalyticsHelpers } from '../analytics.helpers';
import { FilterBlock } from '../filter-block';
import './account.analytics.scss';
import { useEffectExceptOnMount } from '../../../modules/core';
import { analyticsFilterSelected } from '../analytics.utils';
import { AnalyticsPagesForTracking, DatePeriod } from '../analytics.constants';
const colorService = new ColorService();
export const Analytics = () => {
    const data = useSelector(getAnalytics);
    const user = useSelector(selectUser);
    const institution = useSelector(selectInstitution);
    const isUserInstitutionOwner = user.institutionsMemberships.some((im) => im.role === 'owner' && im.institutionId === institution?.id);
    const isUserInstitutionAdmin = user.institutionsMemberships.some((im) => im.role === 'admin' && im.institutionId === institution?.id);
    const isFilterAvailable = institution && (isUserInstitutionAdmin || isUserInstitutionOwner);
    const dispatch = useDispatch();
    const intl = useIntl();
    const dataForInfo = useSelector(getAnalyticsOverview);
    const { providersIds, roomsIds, locationIds } = isFilterAvailable
        ? useSelector(getAnalyticsFilter)
        : { providersIds: [user.id], roomsIds: [], locationIds: [] };
    const datePeriod = useSelector(getAnalyticsDatePeriod);
    const datePickerOptions = AnalyticsHelpers.optionsForDatepicker(intl);
    const cn = AnalyticsHelpers.makeAnalyticsClass('block');
    let monthName = '';
    let dataForRender = data.dataByDate
        .map(item => {
        const totalDurationPerMonth = TimeConverter.millisecondsToMinutes(item.totalDurationPerMonth);
        return { ...item, totalDurationPerMonth };
    });
    const lastMonth = _.last(dataForRender);
    if (lastMonth && lastMonth.date) {
        const month = lastMonth.date.replace(/\d+/g, '').trim();
        monthName = intl.formatMessage({ id: `account.analytics.months.${month}` });
    }
    useEffectExceptOnMount(() => {
        dispatch(analyticsFilterSelected({ page: AnalyticsPagesForTracking.USAGE }));
    }, [
        datePeriod,
        isFilterAvailable && providersIds,
        isFilterAvailable && roomsIds,
        isFilterAvailable && locationIds,
    ]);
    useEffect(() => {
        if (!(datePeriod === DatePeriod.THIS_MONTH &&
            providersIds.length === 0 &&
            roomsIds.length === 0 &&
            locationIds.length === 0)) {
            dispatch(analyticsFilterSelected({ page: AnalyticsPagesForTracking.USAGE }));
        }
    }, []);
    useEffect(() => {
        const { start, end } = AnalyticsHelpers.createPeriod(datePickerOptions.find(item => item.key === datePeriod));
        dispatch(getAnalyticsOverviewAPI({
            start,
            end,
            providersIds,
            roomsIds,
            locationIds,
        }));
    }, [
        datePeriod,
        isFilterAvailable && providersIds,
        isFilterAvailable && roomsIds,
        isFilterAvailable && locationIds,
    ]);
    useEffect(() => {
        dispatch(getAnalyticsAPI({ providersIds, roomsIds, locationIds }));
    }, [
        isFilterAvailable && providersIds,
        isFilterAvailable && roomsIds,
        isFilterAvailable && locationIds,
    ]);
    const selectDate = useCallback((selectedOption) => {
        dispatch(setAnalyticsDatePeriod(selectedOption.key));
    }, []);
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const setFilter = useCallback(({ providersIds, roomsIds, locationIds }) => {
        dispatch(setAnalyticsFilter({ providersIds, roomsIds, locationIds }));
    }, []);
    return (<div className={cn('wrapper')}>
      <div className={cn('header')}>
        <h3 className={cn('header-text')}><FormattedMessage id="account.analytics.title"/></h3>
        {isFilterAvailable
            &&
                <FilterBlock onChange={setFilter} selectedProvidersIds={providersIds} selectedRoomsIds={roomsIds} selectedLocationsIds={locationIds}/>}
      </div>
      <div className="tabs">
        <AnalyticsTabs />
        <div className="tabs__content" data-test-id="analytics-tab">
          <DatepickerBlock options={datePickerOptions} selectedOptionKey={datePeriod} onChange={selectDate}/>
          <InfoBlock month={dataForInfo}/>
          <BarChartBlock barsColor={colorService.toRGBString(_.get(institution, 'frontColor') || '#66cccc')} dataForRender={dataForRender}/>
          <UsageHighlightsBlock data={data} monthName={monthName}/>
          <ShareBlock user={user}/>
        </div>
      </div>
    </div>);
};
